import { AlertEntry } from '../../../../../development/alerts.service';
import { AlertModel } from '@models/alert.model';
import { AppType } from '@models/alert-monitoring.model';
import { AlertsV2ShowSettings, FlowLookup } from '@models/alerts-v2.model';
import { IntelligenceModels } from '@models/intelligence.model';
import { AnalyticClasses } from '@enums/alert-events.enum';
import { RelativeTime, UiDatetimeRangePickerModel } from '@ui';

const PANIC_ALERT_CAMERA_ID = 'panicAlert';
const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

export interface LiveAlertFilterContext {
  cutoffTs: number;
  orgCameraIds: string[];
  orgEdgeIds: string[];
  eventIds: string[];
  tagIds: string[];
  tagToEventIds: Record<string, string[]>;
  cameraIds: string[];
  trackedObjects: AnalyticClasses[];
  flowTypes: FlowLookup[];
  acknowledges: boolean[];
}

export function relativeRangeToMs(relative: RelativeTime): number {
  switch (relative.unit) {
    case UiDatetimeRangePickerModel.CustomUnit.minutes:
      return relative.value! * MINUTE_MS;
    case UiDatetimeRangePickerModel.CustomUnit.hours:
      return relative.value! * HOUR_MS;
    case UiDatetimeRangePickerModel.CustomUnit.days:
      return relative.value! * DAY_MS;
    case UiDatetimeRangePickerModel.CustomUnit.weeks:
      return relative.value! * WEEK_MS;
  }
}

export function filterIncomingLiveAlerts(
  alerts: AlertEntry[],
  context: LiveAlertFilterContext,
): AlertEntry[] {
  let filtered = alerts.filter((alert) => alert.timestamp > context.cutoffTs);
  filtered = filtered.filter(
    (alert) => alert.appType == null || alert.appType === AppType.VMS,
  );
  filtered = filtered.filter((alert) => {
    if (alert.cameraId === PANIC_ALERT_CAMERA_ID) return true;
    if (alert.cameraId) return context.orgCameraIds.includes(alert.cameraId);
    return context.orgEdgeIds.includes(alert.edgeId);
  });
  filtered = AlertModel.filterAlertsByShowSettings(
    filtered,
    AlertsV2ShowSettings.ALERT_PAGE,
  );

  const tagEventIds = new Set<string>();
  context.tagIds.forEach((tagId) => {
    (context.tagToEventIds[tagId] ?? []).forEach((eventId) =>
      tagEventIds.add(eventId),
    );
  });
  const eventIds = context.tagIds.length
    ? context.eventIds.length
      ? [...tagEventIds].filter((eventId) => context.eventIds.includes(eventId))
      : [...tagEventIds]
    : context.eventIds;
  if (context.tagIds.length && !eventIds.length) return [];
  if (eventIds.length) {
    filtered = filtered.filter((alert) =>
      eventIds.includes(alert.eventId ?? ''),
    );
  }

  if (context.cameraIds.length) {
    filtered = filtered.filter((alert) =>
      context.cameraIds.includes(alert.cameraId ?? ''),
    );
  }
  if (context.trackedObjects.length) {
    filtered = filtered.filter((alert) =>
      context.trackedObjects.includes(alert.type),
    );
  }
  if (context.flowTypes.length) {
    filtered = filtered.filter((alert) =>
      context.flowTypes.some(
        (flowType) =>
          flowType.category === alert.category &&
          flowType.flowType === alert.flowType,
      ),
    );
  }
  if (
    context.acknowledges.length === 1 &&
    context.acknowledges[0] === false
  ) {
    filtered = filtered.filter((alert) => alert.archived !== true);
  }

  return filtered;
}

export function toLiveAlertItem(
  alert: AlertEntry,
): IntelligenceModels.Alerts.Item {
  return {
    alertId: alert.alertInstanceId?.toString() ?? alert._id,
    monitoringAlert: alert,
    timestamp: alert.timestamp,
    cameraId: alert.cameraId,
    edgeId: alert.edgeId,
    locationId: alert.locationId,
    eventName: alert.eventName,
    alertMessage: alert.alertMessage,
    archived: alert.archived,
    eventTags: alert.eventTags,
  };
}
